import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertCircle, Ban, CheckCircle2, FileText, Link2, Loader2, RotateCcw, Scale, Trash2, Wand2 } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog, Modal } from '@components/Dialog';
import { Switch } from '@components/Switch';
import { ActionButton } from '@components/Table';
import creditCardService from '@services/creditCardService';
import importService from '@services/importService';
import {
  IMPORT_FILE_FORMAT_LABELS,
  IMPORT_LINE_STATUS_LABELS,
  INVOICE_STATUS_LABELS,
  type Category,
  type ImportBatch,
  type ImportBatchInvoice,
  type ImportLine,
  type ImportLineStatus,
  type ReconcileCandidate,
} from '@/types/finance';
import { addCompetence, competenceLabel, competenceOf, competenceToKey, currentCompetence } from '@/utils/competence';
import { formatDateBR, formatDateTimeBR } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { AmountText, CategorySelect } from './financeBits';
import { ImportRuleFormModal } from './ImportRuleFormModal';

interface Props {
  batch: ImportBatch;
  categories: Category[];
  /** Lote alterado (patch de linha, regras reaplicadas, confirmado, descartado). */
  onBatchChange: (batch: ImportBatch) => void;
  /** Voltar à etapa de escolha do arquivo (depois de confirmar/descartar). */
  onDone: () => void;
  onError: (err: unknown) => void;
}

const STATUS_BADGE: Record<ImportLineStatus, string> = {
  new: 'badge-success',
  existing: 'badge-neutral',
  ignored: 'badge-warning',
  imported: 'badge-info',
};

const STATUS_HINT: Record<ImportLineStatus, string> = {
  new: 'Vai virar um lançamento (ou item de fatura) ao confirmar',
  existing: 'Já existe um lançamento igual na conta/cartão (não será importada)',
  ignored: 'Você pediu para não importar',
  imported: 'Importada',
};

/** "mesmo dia" / "2 dias de diferença" e "mesmo valor" / "R$ 12,30 de diferença" (sem sinal: o contrato não fixa a direção). */
const reconcileDiffText = (c: ReconcileCandidate): string => {
  const days = Math.abs(c.days_diff ?? 0);
  const cents = Math.abs(c.amount_diff_cents ?? 0);
  const d = days === 0 ? 'mesmo dia' : `${days} ${days === 1 ? 'dia' : 'dias'} de diferença`;
  const v = cents === 0 ? 'mesmo valor' : `${formatBRL(cents)} de diferença`;
  return `${d} · ${v}`;
};

const reconcileOptionLabel = (c: ReconcileCandidate): string =>
  `${formatDateBR(c.transaction_date)} · ${c.description} · ${formatBRL(c.amount_cents)}${c.is_scheduled ? ' · agendada' : ''}`;

/**
 * Prévia de um lote (`previewing`): linhas editáveis (descrição, categoria, ignorar/restaurar;
 * em lote de cartão também a fatura-alvo), contadores, "criar regra a partir desta linha"
 * (+ reaplicar regras) e confirmar/descartar. Lotes já confirmados/descartados aparecem só para
 * leitura. Lote de conta vindo de PDF de extrato (Itaú) traz `statement_balance_*`: a prévia mostra o
 * saldo do extrato e a confirmação oferece o Switch `update_balance` (ajuste de saldo na data do extrato).
 * Fase 7 (lote de conta): cada linha nova pode "Conciliar com" um lançamento previsto (recorrência ou
 * agendado) — o previsto vira realizado em vez de duplicar; "Não conciliar" manda `reconcile_transaction_id: 0`.
 */
export const ImportPreview: React.FC<Props> = ({ batch, categories, onBatchChange, onDone, onError }) => {
  const editable = batch.status === 'previewing';
  const isCard = batch.target_type === 'card';
  /** Saldo em conta lido do extrato em PDF (só lote de conta). */
  const hasStatementBalance = !isCard && batch.statement_balance_cents !== null && batch.statement_balance_cents !== undefined;
  const statementBalanceText = hasStatementBalance
    ? `${formatBRL(batch.statement_balance_cents)}${batch.statement_balance_date ? ` em ${formatDateBR(batch.statement_balance_date)}` : ''}`
    : '';
  const [busyLine, setBusyLine] = useState<number | null>(null);
  const [descriptions, setDescriptions] = useState<Record<number, string>>({});
  const [ruleFrom, setRuleFrom] = useState<ImportLine | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [acting, setActing] = useState(false);
  const [reapplying, setReapplying] = useState(false);
  const [generateFuture, setGenerateFuture] = useState(true);
  /** Lote de conta com saldo do extrato: gravar o ajuste de saldo ao confirmar (padrão ligado). */
  const [updateBalance, setUpdateBalance] = useState(true);
  /** Faturas do cartão no intervalo do lote ±1 mês (opções da coluna Fatura). */
  const [cardInvoices, setCardInvoices] = useState<ImportBatchInvoice[]>([]);

  useEffect(() => {
    if (!isCard || !editable) return;
    const start = competenceOf(batch.period_start) ?? currentCompetence();
    const end = competenceOf(batch.period_end) ?? start;
    creditCardService
      .getInvoices(batch.target_id, competenceToKey(addCompetence(start, -1)), competenceToKey(addCompetence(end, 1)))
      .then((list) => setCardInvoices(list.map((i) => ({ id: i.id, label: i.label, status: i.status }))))
      .catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCard, editable, batch.id, batch.target_id, batch.period_start, batch.period_end]);

  const invoiceOptions = useMemo(() => {
    // As faturas-alvo do lote (batch.invoices) sempre entram, mesmo fora do intervalo.
    const map = new Map<number, ImportBatchInvoice>();
    for (const i of cardInvoices) map.set(i.id, i);
    for (const i of batch.invoices ?? []) if (!map.has(i.id)) map.set(i.id, i);
    return [...map.values()];
  }, [cardInvoices, batch.invoices]);

  const lines = useMemo(() => batch.lines ?? [], [batch.lines]);
  const installmentLines = useMemo(() => lines.filter((l) => l.status === 'new' && l.installment_total && l.installment_total > 1 && (l.installment_index ?? 1) < l.installment_total).length, [lines]);
  const counts = useMemo(() => {
    const c = { new: 0, existing: 0, ignored: 0, imported: 0 } as Record<ImportLineStatus, number>;
    for (const l of lines) c[l.status]++;
    return c;
  }, [lines]);

  /** Linhas novas que vão realizar um previsto (contado aqui para refletir os PATCHes; o lote traz `reconcile_count`). */
  const reconcileCount = useMemo(
    () => (isCard ? 0 : lines.length > 0 ? lines.filter((l) => l.status === 'new' && !!l.reconcile).length : batch.reconcile_count ?? 0),
    [isCard, lines, batch.reconcile_count],
  );
  const createdCount = counts.new - reconcileCount;
  const confirmSummary = `${createdCount > 0 ? `Criar ${createdCount} ${createdCount === 1 ? 'lançamento' : 'lançamentos'}` : 'Nenhum lançamento novo'}${
    reconcileCount > 0 ? ` e realizar ${reconcileCount} ${reconcileCount === 1 ? 'lançamento previsto' : 'lançamentos previstos'} (conciliação)` : ''
  } em ${batch.target_name}?`;

  const replaceLine = (line: ImportLine) => onBatchChange({ ...batch, lines: lines.map((l) => (l.id === line.id ? line : l)) });

  const patch = async (line: ImportLine, p: Parameters<typeof importService.patchLine>[2]) => {
    setBusyLine(line.id);
    try {
      replaceLine(await importService.patchLine(batch.id, line.id, p));
    } catch (err) {
      onError(err);
    } finally {
      setBusyLine(null);
    }
  };

  const commitDescription = (line: ImportLine) => {
    const next = (descriptions[line.id] ?? line.description).trim();
    if (!next || next === line.description) {
      setDescriptions((d) => {
        const { [line.id]: _omit, ...rest } = d;
        return rest;
      });
      return;
    }
    patch(line, { description: next });
  };

  const reapply = async () => {
    setReapplying(true);
    try {
      onBatchChange(await importService.reapplyRules(batch.id));
    } catch (err) {
      onError(err);
    } finally {
      setReapplying(false);
    }
  };

  const confirm = async () => {
    setActing(true);
    try {
      onBatchChange(await importService.confirm(batch.id, isCard ? { generate_future_installments: generateFuture } : hasStatementBalance ? { update_balance: updateBalance } : {}));
      setConfirming(false);
    } catch (err) {
      setConfirming(false);
      onError(err);
    } finally {
      setActing(false);
    }
  };

  const discard = async () => {
    setActing(true);
    try {
      onBatchChange(await importService.discard(batch.id));
      setDiscarding(false);
    } catch (err) {
      setDiscarding(false);
      onError(err);
    } finally {
      setActing(false);
    }
  };

  const categoryCell = (l: ImportLine) => {
    if (!editable || l.status === 'existing') {
      const name = l.chosen_category_name ?? l.suggested_category_name;
      return name ? <span className="text-sm">{name}</span> : <span className="text-slate-400 text-sm">—</span>;
    }
    return (
      <div className="space-y-0.5">
        <CategorySelect
          categories={categories}
          movement={l.amount_cents < 0 ? 'expense' : 'income'}
          value={l.chosen_category_id ?? l.suggested_category_id ?? ''}
          onChange={(id) => patch(l, { chosen_category_id: id === '' ? 0 : id })}
          disabled={busyLine === l.id || l.status === 'ignored'}
          emptyLabel="Sem categoria"
          className="py-1"
        />
        {!l.chosen_category_id && l.suggested_category_name && <p className="text-[11px] text-slate-500">sugerida pela regra</p>}
      </div>
    );
  };

  /** Lote de cartão: fatura-alvo editável (fatura paga aparece, mas não pode ser escolhida). */
  const invoiceCell = (l: ImportLine) => {
    if (!isCard) return null;
    if (!editable || l.status === 'existing') {
      return l.target_invoice_label ? <span className="text-sm whitespace-nowrap">{l.target_invoice_label}</span> : <span className="text-slate-400 text-sm">—</span>;
    }
    const known = invoiceOptions.some((i) => i.id === l.target_invoice_id);
    return (
      <select
        className="select-field py-1"
        value={l.target_invoice_id ?? ''}
        disabled={busyLine === l.id || l.status === 'ignored'}
        onChange={(e) => e.target.value && patch(l, { target_invoice_id: Number(e.target.value) })}
      >
        {!known && l.target_invoice_id && <option value={l.target_invoice_id}>{l.target_invoice_label ?? `Fatura ${l.target_invoice_id}`}</option>}
        {invoiceOptions.map((i) => (
          <option key={i.id} value={i.id} disabled={i.status === 'paid' && i.id !== l.target_invoice_id}>
            {i.label}
            {i.status === 'paid' ? ` (${INVOICE_STATUS_LABELS.paid.toLowerCase()})` : ''}
          </option>
        ))}
      </select>
    );
  };

  const noteText = (l: ImportLine) =>
    l.note ? (
      <p className="text-[11px] text-amber-700 inline-flex items-center gap-1 mt-0.5">
        <AlertCircle size={11} /> {l.note}
      </p>
    ) : null;

  const descriptionCell = (l: ImportLine) =>
    editable && l.status !== 'existing' ? (
      <input
        className="input-field py-1"
        value={descriptions[l.id] ?? l.description}
        maxLength={200}
        disabled={busyLine === l.id || l.status === 'ignored'}
        onChange={(e) => setDescriptions((d) => ({ ...d, [l.id]: e.target.value }))}
        onBlur={() => commitDescription(l)}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
    ) : (
      <span className="text-sm text-slate-800 [overflow-wrap:anywhere]">{l.description}</span>
    );

  const actions = (l: ImportLine) =>
    editable && l.status !== 'existing' ? (
      <div className="flex items-center justify-end">
        {l.status === 'ignored' ? (
          <ActionButton icon={<RotateCcw size={15} />} variant="view" tooltip="Restaurar (importar)" disabled={busyLine === l.id} onClick={() => patch(l, { status: 'new' })} />
        ) : (
          <ActionButton icon={<Ban size={15} />} variant="delete" tooltip="Ignorar (não importar)" disabled={busyLine === l.id} onClick={() => patch(l, { status: 'ignored' })} />
        )}
        <ActionButton icon={<Wand2 size={15} />} variant="default" tooltip="Criar regra a partir desta linha" disabled={busyLine === l.id} onClick={() => setRuleFrom(l)} />
      </div>
    ) : null;

  /**
   * Lote de conta: "Conciliar com" — previsto escolhido (descrição, data, valor, Agendada, diferença) e
   * select entre as opções + "Não conciliar" (PATCH `reconcile_transaction_id`, 0 = não conciliar).
   */
  const reconcileCell = (l: ImportLine) => {
    if (isCard) return null;
    const options = l.reconcile_options ?? [];
    const chosen = l.reconcile ?? null;
    const canEditLine = editable && l.status === 'new';
    if (!chosen && (options.length === 0 || !canEditLine)) return null;
    const known = !chosen || options.some((o) => o.transaction_id === chosen.transaction_id);
    return (
      <div className={clsx('mt-1 rounded-md border px-2 py-1.5 space-y-1', chosen ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50')}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 inline-flex items-center gap-1">
          <Link2 size={11} /> {canEditLine ? 'Conciliar com' : 'Conciliado com'}
        </p>
        {chosen ? (
          <div className="text-xs text-slate-700 space-y-0.5">
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="font-medium text-slate-800 [overflow-wrap:anywhere]">{chosen.description}</span>
              {chosen.is_scheduled && <span className="badge-success">Agendada</span>}
              {chosen.recurring_transaction_id ? <span className="badge-info">Recorrente</span> : null}
            </p>
            <p className="flex flex-wrap items-center gap-x-2">
              <span>{formatDateBR(chosen.transaction_date)}</span>
              <AmountText cents={chosen.amount_cents} />
              {chosen.category_name && <span className="text-slate-500">{chosen.category_name}</span>}
            </p>
            <p className="text-slate-500">{reconcileDiffText(chosen)} — o previsto recebe a data e o valor do banco.</p>
          </div>
        ) : (
          <p className="text-xs text-slate-500">Não conciliar: entra como lançamento novo.</p>
        )}
        {canEditLine && (
          <select
            className="select-field py-1 text-xs"
            aria-label="Conciliar com"
            value={chosen?.transaction_id ?? 0}
            disabled={busyLine === l.id}
            onChange={(e) => patch(l, { reconcile_transaction_id: Number(e.target.value) })}
          >
            {!known && chosen && <option value={chosen.transaction_id}>{reconcileOptionLabel(chosen)}</option>}
            {options.map((o) => (
              <option key={o.transaction_id} value={o.transaction_id}>
                {reconcileOptionLabel(o)}
              </option>
            ))}
            <option value={0}>Não conciliar (lançamento novo)</option>
          </select>
        )}
      </div>
    );
  };

  const statusBadge = (l: ImportLine) => (
    <span className={STATUS_BADGE[l.status]} title={STATUS_HINT[l.status]}>
      {IMPORT_LINE_STATUS_LABELS[l.status]}
      {l.installment_total ? ` ${l.installment_index}/${l.installment_total}` : ''}
    </span>
  );

  return (
    <div className="space-y-4">
      {/* Cabeçalho do lote */}
      <div className="card p-3 sm:p-4 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium text-slate-800 [overflow-wrap:anywhere]">{batch.file_name}</p>
            <p className="text-small">
              {IMPORT_FILE_FORMAT_LABELS[batch.file_format]} · {isCard ? 'cartão' : 'conta'} <strong>{batch.target_name}</strong>
              {batch.period_start && batch.period_end && ` · ${formatDateBR(batch.period_start)} a ${formatDateBR(batch.period_end)}`}
              {` · lido em ${formatDateTimeBR(batch.created_at)}`}
            </p>
            {batch.file_format === 'pdf' && (batch.pdf_due_date || batch.pdf_competence) && (
              <p className="text-xs text-sky-800 inline-flex items-center gap-1 mt-1" title="Lido do cabeçalho do PDF: todas as linhas foram para essa fatura quando ela existe no cartão">
                <FileText size={12} />
                {batch.pdf_due_date ? `Fatura com vencimento em ${formatDateBR(batch.pdf_due_date)} reconhecida` : 'Fatura reconhecida'}
                {batch.pdf_competence ? ` · competência ${competenceLabel(batch.pdf_competence)}` : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="badge-success">{counts.new} novas</span>
            <span className="badge-neutral">{counts.existing} já existem</span>
            <span className="badge-warning">{counts.ignored} ignoradas</span>
            {counts.imported > 0 && <span className="badge-info">{counts.imported} importadas</span>}
          </div>
        </div>

        {editable && reconcileCount > 0 && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 flex items-start gap-2">
            <Link2 size={15} className="flex-shrink-0 mt-0.5" />
            <span>
              <strong>{reconcileCount}</strong> {reconcileCount === 1 ? 'lançamento previsto será realizado' : 'lançamentos previstos serão realizados'} pelo extrato (conciliação com
              recorrências/agendados, sem duplicar). Confira em "Conciliar com" em cada linha.
            </span>
          </div>
        )}

        {hasStatementBalance && (
          <div
            className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900 flex items-center gap-2"
            title={editable ? 'Lido do cabeçalho do extrato. Ao confirmar, pode virar um ajuste de saldo da conta nessa data.' : 'Lido do cabeçalho do extrato.'}
          >
            <Scale size={15} className="flex-shrink-0" />
            <span>
              Saldo em conta no extrato: <strong>{statementBalanceText}</strong>
              {editable && <span className="text-sky-800"> — ao confirmar, pode ajustar o saldo da conta nessa data.</span>}
            </span>
          </div>
        )}

        {batch.status === 'confirmed' && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex flex-wrap items-center gap-2">
            <CheckCircle2 size={16} />
            <span>
              Importação concluída: <strong>{batch.imported_count}</strong>{' '}
              {isCard ? (batch.imported_count === 1 ? 'item criado' : 'itens criados') : batch.imported_count === 1 ? 'lançamento criado' : 'lançamentos criados'} em {batch.target_name}.
              {batch.balance_updated && hasStatementBalance && (
                <>
                  {' '}
                  Saldo da conta ajustado para <strong>{statementBalanceText}</strong> (
                  <Link to={`/finance/accounts?open=${batch.target_id}`} className="text-emerald-700 font-medium hover:underline">
                    ver a conta
                  </Link>
                  ).
                </>
              )}
            </span>
            {isCard ? (
              <Link to={`/finance/cards?open=${batch.target_id}`} className="text-emerald-700 font-medium hover:underline">
                Ver as faturas do cartão
              </Link>
            ) : (
              <Link
                to={`/finance/transactions?account_id=${batch.target_id}${batch.period_start && batch.period_end ? `&start=${batch.period_start}&end=${batch.period_end}` : ''}`}
                className="text-emerald-700 font-medium hover:underline"
              >
                Ver em Lançamentos
              </Link>
            )}
            <Button variant="outline" size="sm" className="ml-auto" onClick={onDone}>
              Importar outro arquivo
            </Button>
          </div>
        )}
        {batch.status === 'discarded' && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex flex-wrap items-center gap-2">
            <Trash2 size={16} />
            <span>Lote descartado: nada foi importado.</span>
            <Button variant="outline" size="sm" className="ml-auto" onClick={onDone}>
              Importar outro arquivo
            </Button>
          </div>
        )}

        {editable && (
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-2 pt-1">
            <Button variant="primary" size="sm" icon={<CheckCircle2 />} onClick={() => setConfirming(true)} disabled={counts.new === 0 || acting}>
              Confirmar importação ({counts.new})
            </Button>
            <Button variant="outline" size="sm" icon={<Wand2 />} onClick={reapply} loading={reapplying} disabled={acting}>
              Reaplicar regras
            </Button>
            <Button variant="outline" size="sm" icon={<Trash2 />} className="sm:ml-auto text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDiscarding(true)} disabled={acting}>
              Descartar
            </Button>
          </div>
        )}
      </div>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {lines.map((l) => (
          <div key={l.id} className={clsx('card p-3 space-y-2', l.status === 'ignored' && 'opacity-60', l.status === 'existing' && 'bg-slate-50')}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-small whitespace-nowrap">{formatDateBR(l.entry_date)}</span>
              <AmountText cents={l.amount_cents} className="text-sm font-medium" />
            </div>
            {descriptionCell(l)}
            {noteText(l)}
            {reconcileCell(l)}
            <div className="flex items-center justify-between gap-2">
              {statusBadge(l)}
              {actions(l)}
            </div>
            {isCard && (
              <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                <span className="text-small">Fatura</span>
                {invoiceCell(l)}
              </div>
            )}
            {categoryCell(l)}
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th className="w-28">Data</th>
                <th>Descrição</th>
                <th className="text-right">Valor</th>
                <th className="w-28">Situação</th>
                {isCard && <th className="w-40">Fatura</th>}
                <th className="w-64">Categoria</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr>
                  <td colSpan={isCard ? 7 : 6} className="table-cell text-center text-slate-500 py-10">
                    Nenhuma linha lida do arquivo.
                  </td>
                </tr>
              )}
              {lines.map((l) => (
                <tr key={l.id} className={clsx('table-row', l.status === 'ignored' && 'opacity-60', l.status === 'existing' && 'bg-slate-50')}>
                  <td className="table-cell whitespace-nowrap">{formatDateBR(l.entry_date)}</td>
                  <td className="table-cell">
                    {descriptionCell(l)}
                    {noteText(l)}
                    {reconcileCell(l)}
                  </td>
                  <td className="table-cell text-right">
                    <AmountText cents={l.amount_cents} className="font-medium" />
                  </td>
                  <td className="table-cell">{statusBadge(l)}</td>
                  {isCard && <td className="table-cell">{invoiceCell(l)}</td>}
                  <td className="table-cell">{categoryCell(l)}</td>
                  <td className="table-cell">
                    {busyLine === l.id ? <Loader2 size={16} className="animate-spin text-slate-400 ml-auto" /> : actions(l)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {ruleFrom && (
        <ImportRuleFormModal
          categories={categories}
          prefill={{
            pattern: ruleFrom.description,
            category_id: ruleFrom.chosen_category_id ?? ruleFrom.suggested_category_id ?? undefined,
            applies_to: isCard ? 'card' : 'both',
            movement: ruleFrom.amount_cents < 0 ? 'expense' : 'income',
          }}
          onClose={() => setRuleFrom(null)}
          onSaved={() => {
            setRuleFrom(null);
            reapply();
          }}
          onError={onError}
        />
      )}

      {isCard ? (
        <Modal isOpen={confirming} onClose={() => !acting && setConfirming(false)} size="sm" closeOnBackdrop={!acting} showCloseButton={!acting}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-h3">Confirmar importação da fatura</h2>
          </div>
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-3 text-sm text-slate-700">
            <p>
              Criar {counts.new} {counts.new === 1 ? 'item' : 'itens'} nas faturas de <strong>{batch.target_name}</strong>? As linhas "já existe" e "ignorada" não entram.
            </p>
            <Switch checked={generateFuture} onChange={setGenerateFuture} label="Gerar parcelas futuras das compras parceladas" />
            <p className="text-small">
              Linhas com sufixo "i/N" ({installmentLines} {installmentLines === 1 ? 'linha' : 'linhas'}) geram também as parcelas seguintes nas próximas faturas. Ao importar a fatura
              seguinte, elas são reconhecidas como "já existe".
            </p>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={acting}>
              Cancelar
            </Button>
            <Button variant="success" onClick={confirm} loading={acting}>
              Importar
            </Button>
          </div>
        </Modal>
      ) : hasStatementBalance ? (
        <Modal isOpen={confirming} onClose={() => !acting && setConfirming(false)} size="sm" closeOnBackdrop={!acting} showCloseButton={!acting}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-h3">Confirmar importação do extrato</h2>
          </div>
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-3 text-sm text-slate-700">
            <p>{confirmSummary} As linhas "já existe" e "ignorada" não entram.</p>
            <Switch
              checked={updateBalance}
              onChange={setUpdateBalance}
              label={`Atualizar o saldo da conta com o saldo do extrato (ajuste de saldo${batch.statement_balance_date ? ` em ${formatDateBR(batch.statement_balance_date)}` : ''})`}
            />
            <p className="text-small">
              O extrato informa saldo em conta de <strong>{statementBalanceText}</strong>. Ligado, o sistema grava um ajuste de saldo nessa data — o mesmo que "Ajustar saldo" em Contas
              (um ajuste já existente na mesma data é substituído). Desligue se preferir conferir o saldo por conta própria.
            </p>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={acting}>
              Cancelar
            </Button>
            <Button variant="success" onClick={confirm} loading={acting}>
              Importar
            </Button>
          </div>
        </Modal>
      ) : (
        <ConfirmDialog
          isOpen={confirming}
          onClose={() => setConfirming(false)}
          onConfirm={confirm}
          title="Confirmar importação"
          message={`${confirmSummary} As linhas "já existe" e "ignorada" não entram.`}
          confirmText="Importar"
          severity="success"
          loading={acting}
        />
      )}
      <ConfirmDialog
        isOpen={discarding}
        onClose={() => setDiscarding(false)}
        onConfirm={discard}
        title="Descartar lote"
        message="Descartar esta prévia? Nada é importado; você pode ler o arquivo de novo quando quiser."
        confirmText="Descartar"
        severity="danger"
        loading={acting}
      />
    </div>
  );
};
